import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { IMPORT_LOGINS_KEY } from "@via/pages/projects/metadata/configuration/data-import/data-import.api.tsx";
import type { ErrorNotification } from "@via/utils/interfaces.tsx";
import type {
  VmetaAdminImportLogin,
  VmetaAdminImportLoginList,
  VmetaImportLoginMergeBody,
  VmetaImportLoginMergeResult,
} from "common";

const ADMIN_URL = "/api/v1/admin/vmeta/import-logins";
/** Under IMPORT_LOGINS_KEY, so the drawer's own invalidation refreshes this list too. */
const ADMIN_KEY = [IMPORT_LOGINS_KEY, "admin"];

export const useAdminImportLogins = (): UseQueryResult<
  VmetaAdminImportLoginList,
  Error
> =>
  useQuery({
    queryKey: ADMIN_KEY,
    queryFn: async () =>
      (await axios.get<{ data: VmetaAdminImportLoginList }>(ADMIN_URL)).data
        .data,
    retry: false,
  });

export const useMergeImportLogins = (): UseMutationResult<
  VmetaImportLoginMergeResult,
  ErrorNotification,
  VmetaImportLoginMergeBody
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async body =>
      (
        await axios.post<{ data: VmetaImportLoginMergeResult }>(
          `${ADMIN_URL}/merge`,
          body
        )
      ).data.data,
    // onSettled, not onSuccess: a partial failure created a row, so the list must refresh either way.
    onSettled: async () =>
      queryClient.invalidateQueries({ queryKey: [IMPORT_LOGINS_KEY] }),
  });
};

/** Rows that may merge, together, in the server's order. */
export const groupByMergeKey = (
  items: VmetaAdminImportLogin[]
): VmetaAdminImportLogin[][] => {
  const groups = new Map<string, VmetaAdminImportLogin[]>();
  for (const item of items)
    groups.set(item.mergeKey, [...(groups.get(item.mergeKey) ?? []), item]);
  return [...groups.values()];
};
